import { CARD_ACTION_TYPES } from "./card.types.js";

const CARD_INITIAL_STATE = {
    cardData: {},
    checklistData: {},
    checklistCardIds: [], // Array of all card IDs (for pagination/search)
    checklistPagination: {
        hasMore: false,
        isLoading: false,
        lastFetchedCount: 0
    },
    parallelData: [],
    checklistMetadata: { sortOrder: 'numeric', teams: {} }, // Store metadata like sortOrder and teams
    queuedCards: {},
    queuedCardsDisplay: [],
    cardCollection: {},
    isPending: false,
    checklistCache: {}, // Cache for up to 10 checklists: { cacheKey: { checklistData, checklistCardIds, metadata, parallelData, lastAccessed } }
    viewingCardData: null, // Stores the most recent friend's collection data: { friendUID, cardData, checklistData, lastFetchedAt, collectionLastUpdated }
    collectionInvalidationKey: 0 // Bump to trigger refetch of current category collection only
}

export const cardReducer = (state = CARD_INITIAL_STATE, action = {}) => {
    const { type, payload } = action;

    switch (type) {
        case CARD_ACTION_TYPES.SET_CARD_DATA:
            return { ...state, cardData: payload };
        case CARD_ACTION_TYPES.SET_CHECKLIST_DATA:
            return { ...state, checklistData: payload, checklistPagination: { ...state.checklistPagination, lastFetchedCount: Object.keys(payload).length } };
        case CARD_ACTION_TYPES.APPEND_CHECKLIST_DATA:
            return { 
                ...state, 
                checklistData: { ...state.checklistData, ...payload },
                checklistPagination: { ...state.checklistPagination, lastFetchedCount: state.checklistPagination.lastFetchedCount + Object.keys(payload).length }
            };
        case CARD_ACTION_TYPES.SET_CHECKLIST_PAGINATION:
            return { ...state, checklistPagination: { ...state.checklistPagination, ...payload } };
        case CARD_ACTION_TYPES.SET_CHECKLIST_CARD_IDS:
            return { ...state, checklistCardIds: payload };
        case CARD_ACTION_TYPES.SET_PARALLEL_DATA:
            return { ...state, parallelData: payload };
        case CARD_ACTION_TYPES.SET_CHECKLIST_METADATA:
            return { ...state, checklistMetadata: payload };
        case CARD_ACTION_TYPES.SET_QUEUED_CARDS:
            return { ...state, queuedCards: payload };
        case CARD_ACTION_TYPES.SET_QUEUED_CARDS_DISPLAY:
            return { ...state, queuedCardsDisplay: payload };
        case CARD_ACTION_TYPES.SET_CARD_COLLECTION:
            return { ...state, cardCollection: payload, queuedCards: {}, queuedCardsDisplay: [] };
        case CARD_ACTION_TYPES.UPDATE_CARD_QUANTITY:
            return { ...state, cardCollection: payload };
        case CARD_ACTION_TYPES.UPDATE_CARD_DATA_QUANTITY:
            return { ...state, cardData: payload };
        case CARD_ACTION_TYPES.CLEAR_QUEUE:
            return { ...state, queuedCards: {}, queuedCardsDisplay: [] };
        case CARD_ACTION_TYPES.SET_CHECKLIST_CACHE: {
            const { cacheKey, data } = payload;
            const MAX_CACHE_SIZE = 10;
            const newCache = { ...state.checklistCache };
            
            // If cache is full and this key doesn't exist, remove oldest entry
            if (!newCache[cacheKey] && Object.keys(newCache).length >= MAX_CACHE_SIZE) {
                // Find oldest entry by lastAccessed timestamp
                const entries = Object.entries(newCache);
                entries.sort((a, b) => (a[1].lastAccessed || 0) - (b[1].lastAccessed || 0));
                delete newCache[entries[0][0]];
            }
            
            // Add/update cache entry
            newCache[cacheKey] = {
                ...data,
                lastAccessed: Date.now()
            };
            
            return { ...state, checklistCache: newCache };
        }
        case CARD_ACTION_TYPES.CLEAR_CHECKLIST_CACHE:
            return { ...state, checklistCache: {} };
        case CARD_ACTION_TYPES.CLEAR_CHECKLIST_CACHE_BY_SERIES: {
            const { sport, year, manufacturer, series } = payload;
            const newCache = { ...state.checklistCache };
            const seriesPrefix = `checklists/${sport}/${year}/${manufacturer}/${series}`;
            
            // Remove all cache entries that start with this series path
            Object.keys(newCache).forEach(key => {
                if (key.startsWith(seriesPrefix)) {
                    delete newCache[key];
                }
            });
            
            return { ...state, checklistCache: newCache };
        }
        case CARD_ACTION_TYPES.RESET_CARD_STATE:
            return CARD_INITIAL_STATE;
        case CARD_ACTION_TYPES.SET_VIEWING_CARD_DATA:
            return { ...state, viewingCardData: payload };
        case CARD_ACTION_TYPES.CLEAR_VIEWING_CARD_DATA:
            return { ...state, viewingCardData: null };
        case CARD_ACTION_TYPES.INVALIDATE_COLLECTION:
            return { ...state, collectionInvalidationKey: state.collectionInvalidationKey + 1 };
        default:
            return state;
    }
};